import React, { useState, useEffect, useContext } from 'react';
import { 
  TextField, Button, CircularProgress, Alert, Grid, FormControl,
  InputLabel, Select, MenuItem, FormHelperText, Box
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { fr } from 'date-fns/locale';
import { useMutation, useQuery, gql } from '@apollo/client';
import AuthContext from '../contexts/AuthContext';
import { format } from 'date-fns';
import SearchableSelect from './SearchableSelect';
import { StyledFormBox } from './StyledComponents';

// GraphQL queries et mutations
const GET_DEPARTMENTS = gql`
  query GetDepartments {
    departments {
      id
      name
    }
  }
`;

// Requête pour obtenir tous les utilisateurs
const GET_ALL_USERS = gql`
  query GetAllUsers($departmentId: ID) {
    users(role: "HOST_OR_EMPLOYEE", departmentId: $departmentId) {
      id
      firstName
      lastName
      email
      role
      hostAvailabilities {
        id
        dayOfWeek
        startTime
        endTime
        isActive
      }
      department {
        id
        name
      }
    }
  }
`;

// Requête pour obtenir tous les visiteurs
const GET_ALL_VISITORS = gql`
  query GetAllVisitors {
    users(role: "VISITOR") {
      id
      firstName
      lastName
      email
    }
  }
`;

// Requête pour obtenir tous les départements
const GET_ALL_DEPARTMENTS = gql`
  query GetAllDepartments {
    departments {
      id
      name
    }
  }
`;

// Ajout de console.log pour déboguer
console.log("Définition des mutations GraphQL");

const normalizeDayOfWeek = (value) => {
  if (typeof value === 'number') return value;
  if (typeof value === 'string') {
    if (/^\d+$/.test(value)) return Number(value);
    const match = value.match(/(\d+)$/);
    if (match) return Number(match[1]);
  }
  return null;
};

// Mutation pour créer une visite
const CREATE_VISIT = gql`
  mutation CreateVisit(
    $visitor_id: ID
    $visitor_email: String
    $visitor_first_name: String
    $visitor_last_name: String
    $visitor_phone_number: BigInt
    $host_id: ID!
    $department_id: ID!
    $purpose: String!
    $scheduled_at: DateTime!
    $documents: [Upload]
  ) {
    createVisit(
      visitorId: $visitor_id
      visitorEmail: $visitor_email
      visitorFirstName: $visitor_first_name
      visitorLastName: $visitor_last_name
      visitorPhoneNumber: $visitor_phone_number
      hostId: $host_id
      departmentId: $department_id
      purpose: $purpose
      scheduledAt: $scheduled_at
      documents: $documents
    ) {
      success
      message
      visit {
        id
        visitor {
          id
          firstName
          lastName
        }
        host {
          id
          firstName
          lastName
        }
        department {
          id
          name
        }
        purpose
        scheduledAt
        status
      }
    }
  }
`;

// Mutation pour mettre à jour une visite
const UPDATE_VISIT = gql`
  mutation UpdateVisit(
    $id: ID!
    $host_id: ID
    $department_id: ID
    $purpose: String
    $scheduled_at: DateTime
    $status: String
  ) {
    updateVisit(
      id: $id
      hostId: $host_id
      departmentId: $department_id
      purpose: $purpose
      scheduledAt: $scheduled_at
      status: $status
    ) {
      success
      message
      visit {
        id
        visitor {
          id
          firstName
          lastName
        }
        host {
          id
          firstName
          lastName
        }
        department {
          id
          name
        }
        purpose
        scheduledAt
        status
      }
    }
  }
`;

function VisitForm({ onSuccess, initialData, isEdit = false }) {
  const { user } = useContext(AuthContext);
  const [selectedDocuments, setSelectedDocuments] = useState([]);
  const [useNewVisitor, setUseNewVisitor] = useState(false);
  const [newVisitor, setNewVisitor] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
  });
  
  // État initial du formulaire
  const [formValues, setFormValues] = useState({
    visitorId: initialData?.visitor?.id || (user?.role === 'VISITOR' ? user?.id : ''),
    hostId: initialData?.host?.id || '',
    departmentId: initialData?.department?.id || (['MANAGER', 'ACCEUIL'].includes(user?.role) ? user?.department?.id : ''),
    purpose: initialData?.purpose || '',
    scheduledAt: initialData?.scheduled_at ? new Date(initialData.scheduled_at) : new Date(Date.now() + 24 * 60 * 60 * 1000),
    status: initialData?.status || 'PENDING'
  });
  
  // État des champs touchés
  const [touchedFields, setTouchedFields] = useState({});
  
  // État des erreurs de validation
  const [formErrors, setFormErrors] = useState({});
  
  // État des messages
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  
  // Récupérer la liste des départements
  const { data: departmentsData, loading: departmentsLoading } = useQuery(GET_ALL_DEPARTMENTS);
  
  // Récupérer tous les utilisateurs
  const { data: usersData, loading: usersLoading } = useQuery(GET_ALL_USERS, {
    variables: {
      departmentId: formValues.departmentId
    },
    fetchPolicy: 'network-only',
    skip: !formValues.departmentId
  });
  
  // Récupérer tous les visiteurs
  const { data: visitorsData, loading: visitorsLoading } = useQuery(GET_ALL_VISITORS, {
    fetchPolicy: 'network-only'
  });
  
  // Filtrer les employés pouvant être assignés (hôte ou employé)
  const filteredHosts = usersData?.users || [];
  const selectedHost = filteredHosts.find((host) => String(host.id) === String(formValues.hostId));
  const shouldApplyHostAvailability = selectedHost?.role === 'HOST';

  const getHostSlotsForDate = (date) => {
    if (!selectedHost?.hostAvailabilities?.length || !date) return [];
    const weekday = (date.getDay() + 6) % 7; // JS Sunday=0 -> Monday=0
    return selectedHost.hostAvailabilities
      .filter((slot) => slot.isActive && normalizeDayOfWeek(slot.dayOfWeek) === weekday)
      .map((slot) => {
        const [startHour, startMinute] = String(slot.startTime).split(':').map(Number);
        const [endHour, endMinute] = String(slot.endTime).split(':').map(Number);
        return { startHour, startMinute, endHour, endMinute };
      });
  };
  
  // Liste des visiteurs
  const visitorUsers = visitorsData?.users || [];
  
  // Mutation pour créer une visite
  const [createVisit, { loading: createLoading }] = useMutation(CREATE_VISIT, {
    onCompleted: (data) => {
      console.log("Réponse de création:", data);
      if (data.createVisit.success) {
        setSuccessMessage(data.createVisit.message || "Visite créée avec succès");
        setErrorMessage('');
        setFormValues({
          visitorId: user?.id || '',
          hostId: '',
          departmentId: '',
          purpose: '',
          scheduledAt: new Date(Date.now() + 24 * 60 * 60 * 1000),
          status: 'PENDING'
        });
        setSelectedDocuments([]);
        setUseNewVisitor(false);
        setNewVisitor({
          firstName: '',
          lastName: '',
          email: '',
          phoneNumber: '',
        });
        setTouchedFields({});
        if (onSuccess) onSuccess(data.createVisit.visit);
      } else {
        setErrorMessage(data.createVisit.message || "Une erreur est survenue lors de la création de la visite");
        setSuccessMessage('');
      }
    },
    onError: (error) => {
      console.error("Erreur lors de la création:", error);
      setErrorMessage(`Erreur: ${error.message}`);
      setSuccessMessage('');
    }
  });
  
  // Mutation pour mettre à jour une visite
  const [updateVisit, { loading: updateLoading }] = useMutation(UPDATE_VISIT, {
    onCompleted: (data) => {
      console.log("Réponse de mise à jour:", data);
      if (data.updateVisit.success) {
        setSuccessMessage(data.updateVisit.message || "Visite mise à jour avec succès");
        setErrorMessage('');
        if (onSuccess) onSuccess(data.updateVisit.visit);
      } else {
        setErrorMessage(data.updateVisit.message || "Une erreur est survenue lors de la mise à jour de la visite");
        setSuccessMessage('');
      }
    },
    onError: (error) => {
      console.error("Erreur lors de la mise à jour:", error);
      setErrorMessage(`Erreur: ${error.message}`);
      setSuccessMessage('');
    }
  });
  
  // Forcer la valeur du département pour les gestionnaires
  useEffect(() => {
    if (['MANAGER', 'ACCEUIL'].includes(user?.role) && user?.department?.id && !departmentsLoading) {
      console.log("Setting department for manager:", user.department.id);
      setFormValues(prev => ({
        ...prev,
        departmentId: user.department.id
      }));
    }
  }, [user, departmentsLoading]);

  // Charger les hôtes quand le département change
  useEffect(() => {
    if (formValues.departmentId) {
      console.log("Department changed, loading hosts for department:", formValues.departmentId);
    }
  }, [formValues.departmentId]);

  // Charger les visiteurs quand le département change
  useEffect(() => {
    if (formValues.departmentId) {
      console.log("Department changed, loading visitors for department:", formValues.departmentId);
    }
  }, [formValues.departmentId]);
  
  // Gérer les changements de champs
  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Si c'est un gestionnaire, forcer la valeur du département
    if (['MANAGER', 'ACCEUIL'].includes(user?.role) && name === 'departmentId') {
      return;
    }
    
    // Si le département change, réinitialiser le champ hôte
    if (name === 'departmentId') {
      setFormValues({
        ...formValues,
        [name]: value,
        hostId: '' // Réinitialiser le champ hôte
      });
    } else {
      // Mettre à jour les valeurs du formulaire
      setFormValues({
        ...formValues,
        [name]: value
      });
    }
    
    // Marquer le champ comme touché
    setTouchedFields({
      ...touchedFields,
      [name]: true
    });
    
    // Valider le champ
    validateField(name, value);
  };
  
  // Gérer les changements de date
  const handleDateChange = (date) => {
    // Mettre à jour les valeurs du formulaire
    setFormValues({
      ...formValues,
      scheduledAt: date
    });
    
    // Marquer le champ comme touché
    setTouchedFields({
      ...touchedFields,
      scheduledAt: true
    });
    
    // Valider le champ
    validateField('scheduledAt', date);
  };
  
  // Gérer la perte de focus
  const handleBlur = (e) => {
    const { name, value } = e.target;
    
    // Marquer le champ comme touché
    setTouchedFields({
      ...touchedFields,
      [name]: true
    });
    
    // Valider le champ
    validateField(name, value);
  };

  const handleDocumentsChange = (event) => {
    const files = Array.from(event.target.files || []);
    if (files.length > 3) {
      setErrorMessage('Vous pouvez joindre au maximum 3 fichiers');
      setSelectedDocuments(files.slice(0, 3));
      return;
    }
    setErrorMessage('');
    setSelectedDocuments(files);
  };

  const toNumberOrNull = (value) => {
    if (!value) return null;
    const digits = String(value).replace(/\D/g, '');
    return digits || null;
  };
  
  // Valider un champ spécifique
  const validateField = (name, value) => {
    let errors = { ...formErrors };
    
    switch (name) {
      case 'departmentId':
        if (!value) {
          errors[name] = "Le département est requis";
        } else {
          delete errors[name];
        }
        break;
        
      case 'hostId':
        if (!value) {
          errors[name] = "L'hôte est requis";
        } else {
          delete errors[name];
        }
        break;
        
      case 'purpose':
        if (!value) {
          errors[name] = "Le motif de la visite est requis";
        } else if (value.length < 10) {
          errors[name] = "Le motif doit contenir au moins 10 caractères";
        } else {
          delete errors[name];
        }
        break;
        
      case 'scheduledAt':
        if (!value) {
          errors[name] = "La date et l'heure sont requises";
        } else if (new Date(value) < new Date()) {
          errors[name] = "La date doit être dans le futur";
        } else {
          delete errors[name];
        }
        break;
        
      default:
        break;
    }
    
    setFormErrors(errors);
    return !errors[name];
  };
  
  // Valider le formulaire entier
  const validateForm = () => {
    let isValid = true;
    let newErrors = {};
    let allTouched = {};
    
    // Valider chaque champ
    Object.entries(formValues).forEach(([name, value]) => {
      if (name === 'id') return; // Ignorer l'ID
      if (name === 'status' && !isEdit) return; // Ignorer le statut pour la création
      
      allTouched[name] = true;
      
      if (!validateField(name, value)) {
        isValid = false;
        newErrors[name] = formErrors[name];
      }
    });
    
    // Mettre à jour les champs touchés
    setTouchedFields(allTouched);
    
    return isValid;
  };
  
  // Gérer la soumission du formulaire
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (initialData) {
        // Mise à jour d'une visite existante
        console.log("Mise à jour de la visite:", formValues);
        const variables = {
          id: initialData.id,
          host_id: formValues.hostId,
          department_id: formValues.departmentId,
          purpose: formValues.purpose,
          scheduled_at: format(formValues.scheduledAt, "yyyy-MM-dd'T'HH:mm:ss.SSSxxx"),
          status: formValues.status
        };
        console.log("Variables de mise à jour:", variables);
        const { data } = await updateVisit({
          variables
        });
        console.log("Réponse de mise à jour:", data);
      } else {
        // Création d'une nouvelle visite
        console.log("Création d'une nouvelle visite:", formValues);
        if (selectedDocuments.length > 3) {
          setErrorMessage('Vous pouvez joindre au maximum 3 fichiers');
          return;
        }
        if (!formValues.departmentId || !formValues.hostId || !formValues.purpose || !formValues.scheduledAt) {
          setErrorMessage('Veuillez renseigner tous les champs obligatoires');
          return;
        }
        if (!useNewVisitor && !formValues.visitorId) {
          setErrorMessage('Veuillez sélectionner un visiteur');
          return;
        }
        if (useNewVisitor && (!newVisitor.firstName || !newVisitor.lastName || !newVisitor.email)) {
          setErrorMessage('Veuillez renseigner prénom, nom et email du nouveau visiteur');
          return;
        }

        const variables = {
          visitor_id: useNewVisitor ? null : (user?.role === 'VISITOR' ? user?.id : formValues.visitorId),
          visitor_email: useNewVisitor ? newVisitor.email : null,
          visitor_first_name: useNewVisitor ? newVisitor.firstName : null,
          visitor_last_name: useNewVisitor ? newVisitor.lastName : null,
          visitor_phone_number: useNewVisitor ? toNumberOrNull(newVisitor.phoneNumber) : null,
          host_id: formValues.hostId,
          department_id: formValues.departmentId,
          purpose: formValues.purpose,
          scheduled_at: format(formValues.scheduledAt, "yyyy-MM-dd'T'HH:mm:ss.SSSxxx"),
          documents: selectedDocuments.length ? selectedDocuments : null
        };
        console.log("Variables de création:", variables);
        const { data } = await createVisit({
          variables
        });
        console.log("Réponse de création:", data);
      }
      onSuccess();
    } catch (error) {
      console.error("Erreur lors de la soumission:", error);
      setErrorMessage(error.message);
    }
  };
  
  // Afficher les données de débogage
  console.log("User:", user);
  console.log("Department ID from user:", user?.department?.id);
  console.log("Form values:", formValues);
  console.log("Departments data:", departmentsData);
  
  // Debug: Afficher les informations de debug
  console.log('VisitForm - User role:', user?.role);
  console.log('VisitForm - Visitors data:', visitorsData);
  console.log('VisitForm - Visitor users:', visitorUsers);
  console.log('VisitForm - Form values:', formValues);
  console.log('VisitForm - Filtered hosts:', filteredHosts);
  console.log('VisitForm - Department ID:', formValues.departmentId);
  
  return (
    <StyledFormBox sx={{ mt: 2 }}>
      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {successMessage}
        </Alert>
      )}
      
      {errorMessage && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {errorMessage}
        </Alert>
      )}
      
      <form onSubmit={handleSubmit}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <SearchableSelect
              margin="normal"
              label="Département"
              value={['MANAGER', 'ACCEUIL'].includes(user?.role) ? (user?.department?.id || '') : (formValues.departmentId || '')}
              onChange={(value) => {
                setFormValues(prev => ({ ...prev, departmentId: value }));
                setTouchedFields(prev => ({ ...prev, departmentId: true }));
              }}
              options={departmentsData?.departments?.filter(department => department != null) || []}
              getOptionLabel={(option) => option.name}
              getOptionValue={(option) => option.id}
              required
              disabled={departmentsLoading || ['MANAGER', 'ACCEUIL'].includes(user?.role)}
              error={touchedFields.departmentId && Boolean(formErrors.departmentId)}
              helperText={touchedFields.departmentId && formErrors.departmentId}
              noOptionsText="Aucune option disponible"
            />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <SearchableSelect
              margin="normal"
              label="Hôte / Employé"
              value={formValues.hostId || ''}
              onChange={(value) => {
                setFormValues(prev => ({ ...prev, hostId: value }));
                setTouchedFields(prev => ({ ...prev, hostId: true }));
              }}
              options={filteredHosts.filter(host => host != null)}
              getOptionLabel={(option) => `${option.firstName} ${option.lastName} (${option.role === 'HOST' ? 'Hôte' : 'Employé'})`}
              getOptionValue={(option) => option.id}
              required
              disabled={!formValues.departmentId || usersLoading}
              error={touchedFields.hostId && Boolean(formErrors.hostId)}
              helperText={touchedFields.hostId && formErrors.hostId}
              loading={usersLoading}
              noOptionsText={usersLoading ? "Chargement des employés..." : "Aucun employé disponible"}
            />
          </Grid>
          
          <Grid item xs={12}>
            <TextField
              fullWidth
              margin="normal"
              id="purpose"
              name="purpose"
              label="Motif de la visite"
              multiline
              rows={4}
              value={formValues.purpose}
              onChange={handleChange}
              onBlur={handleBlur}
              error={touchedFields.purpose && Boolean(formErrors.purpose)}
              helperText={touchedFields.purpose && formErrors.purpose}
              required
            />
            {!isEdit && (
              <Box sx={{ mt: 1 }}>
                <Button variant="outlined" component="label" size="small">
                  Joindre des fichiers (max 3)
                  <input
                    hidden
                    type="file"
                    multiple
                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                    onChange={handleDocumentsChange}
                  />
                </Button>
                <FormHelperText>
                  {selectedDocuments.length
                    ? `${selectedDocuments.length} fichier(s): ${selectedDocuments.map((file) => file.name).join(', ')}`
                    : 'Formats autorisés: PDF, DOC, DOCX, JPG, PNG'}
                </FormHelperText>
              </Box>
            )}
          </Grid>

          {['ADMIN', 'MANAGER', 'ACCEUIL'].includes(user?.role) && (
            <Grid item xs={12}>
              {!useNewVisitor && (
                <SearchableSelect
                  margin="normal"
                  label="Visiteur"
                  value={formValues.visitorId || ''}
                  onChange={(value) => {
                    setFormValues(prev => ({ ...prev, visitorId: value }));
                    setTouchedFields(prev => ({ ...prev, visitorId: true }));
                  }}
                  options={visitorUsers.filter(visitor => visitor != null)}
                  getOptionLabel={(option) => `${option.firstName} ${option.lastName} (${option.email})`}
                  getOptionValue={(option) => option.id}
                  required
                  disabled={visitorsLoading}
                  loading={visitorsLoading}
                  noOptionsText={visitorsLoading ? "Chargement des visiteurs..." : "Aucun visiteur disponible"}
                />
              )}
              <Button
                variant="text"
                size="small"
                onClick={() => {
                  setUseNewVisitor((prev) => !prev);
                  setErrorMessage('');
                }}
                sx={{ mt: 0.5 }}
              >
                {useNewVisitor ? 'Choisir un visiteur existant' : 'Visiteur non trouvé ? Ajouter un nouveau'}
              </Button>
              {useNewVisitor && (
                <Grid container spacing={1} sx={{ mt: 0.5 }}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Prénom visiteur"
                      value={newVisitor.firstName}
                      onChange={(e) => setNewVisitor(prev => ({ ...prev, firstName: e.target.value }))}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Nom visiteur"
                      value={newVisitor.lastName}
                      onChange={(e) => setNewVisitor(prev => ({ ...prev, lastName: e.target.value }))}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      size="small"
                      type="email"
                      label="Email visiteur"
                      value={newVisitor.email}
                      onChange={(e) => setNewVisitor(prev => ({ ...prev, email: e.target.value }))}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Téléphone visiteur"
                      value={newVisitor.phoneNumber}
                      onChange={(e) => setNewVisitor(prev => ({ ...prev, phoneNumber: e.target.value }))}
                    />
                  </Grid>
                </Grid>
              )}
            </Grid>
          )}
          
          <Grid item xs={12} md={6}>
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={fr}>
              <DateTimePicker
                label="Date et heure"
                value={new Date(formValues.scheduledAt)}
                onChange={handleDateChange}
                shouldDisableDate={(date) => {
                  if (!selectedHost || !shouldApplyHostAvailability) return false;
                  return getHostSlotsForDate(date).length === 0;
                }}
                shouldDisableTime={(value, view) => {
                  if (!selectedHost || !shouldApplyHostAvailability) return false;
                  const activeDate = new Date(formValues.scheduledAt);
                  const slots = getHostSlotsForDate(activeDate);
                  if (!slots.length) return true;

                  if (view === 'hours') {
                    const hour = typeof value === 'number'
                      ? value
                      : (value?.getHours?.() ?? value?.hour?.() ?? 0);
                    return !slots.some((slot) => {
                      if (hour > slot.startHour && hour < slot.endHour) return true;
                      if (hour === slot.startHour && slot.startMinute < 60) return true;
                      if (hour === slot.endHour && slot.endMinute > 0) return true;
                      return false;
                    });
                  }

                  if (view === 'minutes') {
                    const minute = typeof value === 'number'
                      ? value
                      : (value?.getMinutes?.() ?? value?.minute?.() ?? 0);
                    const hour = new Date(formValues.scheduledAt).getHours();
                    return !slots.some((slot) => {
                      const total = hour * 60 + minute;
                      const startTotal = slot.startHour * 60 + slot.startMinute;
                      const endTotal = slot.endHour * 60 + slot.endMinute;
                      return total >= startTotal && total < endTotal;
                    });
                  }

                  return false;
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    margin="normal"
                    id="scheduledAt"
                    name="scheduledAt"
                    error={touchedFields.scheduledAt && Boolean(formErrors.scheduledAt)}
                    helperText={touchedFields.scheduledAt && formErrors.scheduledAt}
                    required
                  />
                )}
                minDateTime={new Date()}
              />
            </LocalizationProvider>
          </Grid>
          
          
          {isEdit && (
            <Grid item xs={12} md={6}>
              <FormControl 
                fullWidth 
                margin="normal"
                required
              >
                <InputLabel id="status-label">Statut</InputLabel>
                <Select
                  labelId="status-label"
                  id="status"
                  name="status"
                  value={formValues.status}
                  label="Statut"
                  onChange={handleChange}
                >
                  <MenuItem value="PENDING">En attente</MenuItem>
                  <MenuItem value="APPROVED">Approuvée</MenuItem>
                  <MenuItem value="REJECTED">Rejetée</MenuItem>
                  <MenuItem value="COMPLETED">Terminée</MenuItem>
                  {/* <MenuItem value="CANCELLED">Annulée</MenuItem> */}
                </Select>
              </FormControl>
            </Grid>
          )}
          
          {!(isEdit && formValues.status === 'COMPLETED') && (
            <Grid item xs={12}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={createLoading || updateLoading}
                sx={{ mt: 2 }}
              >
                {createLoading || updateLoading ? <CircularProgress size={24} /> : (isEdit ? 'Mettre à jour la visite' : 'Créer la visite')}
              </Button>
            </Grid>
          )}
        </Grid>
      </form>
    </StyledFormBox>
  );
}

export default VisitForm; 
